import type { Role, User } from "@unbox/api";
import { HelpCircle } from "@untitledui/icons";
import { useEffect, useMemo, useRef } from "react";
import { Heading } from "react-aria-components";
import { type SubmitHandler, useForm } from "react-hook-form";
import { useListData } from "react-stately";
import { useAdminMerchants } from "@/api/admin/merchants";
import { useUpdateUser } from "@/api/admin/update-user";
import { useAdminUser } from "@/api/admin/user";
import { useSetUserWorkspaceMemberships } from "@/api/admin/workspace-mutations";
import { useAdminWorkspaces } from "@/api/admin/workspaces";
import { Dialog, Modal, ModalOverlay } from "@/components/application/modals/modal";
import { Button } from "@/components/base/buttons/button";
import { CloseButton } from "@/components/base/buttons/close-button";
import { Input } from "@/components/base/input/input";
import { MultiSelect } from "@/components/base/select/multi-select";
import { Toggle } from "@/components/base/toggle/toggle";
import { Tooltip, TooltipTrigger } from "@/components/base/tooltip/tooltip";
import { FormField, FormItem } from "@/components/ui/form";
import { usePermissions } from "@/hooks/use-permissions";
import { WorkspaceRoleTree, type WorkspaceRoleTreeInfo } from "@/pages/admin/components/workspace-role-tree";
import {
  grantsEqual,
  grantsFromUserWorkspaces,
  useWorkspaceRoleTree,
  type WorkspaceRoleGrants,
} from "@/pages/admin/hooks/use-workspace-role-tree";
import { error, success } from "@/providers/ToastProvider";

type UserWithWorkspaceBrands = User & {
  merchants?: { id: string; name: string; workspaceId?: string | null }[];
  workspaces?: { id: string; name: string }[];
};

type FormValues = {
  // Legacy `agency_user`/`restricted_user` are retired (UNB-1912).
  role: Extract<Role, "admin" | "user" | "paused">;
  firstName: string;
  lastName: string;
};

const editableRole = (role: Role | undefined): FormValues["role"] =>
  role === "admin" || role === "paused" ? role : "user";

type Props = { user?: UserWithWorkspaceBrands; onClose: () => void };

export const EditUserModal = ({ user, onClose }: Props) => {
  const open = user !== undefined;
  const close = onClose;
  const lastUserRef = useRef<UserWithWorkspaceBrands | undefined>(user);
  if (user) lastUserRef.current = user;
  const displayUser = lastUserRef.current;
  // Map any retired legacy role to "user" so the narrowed select stays valid.
  const form = useForm<FormValues>({
    defaultValues: { ...displayUser, role: editableRole(displayUser?.role) },
  });
  const { isSubmitting } = form.formState;
  const { invitableRoles } = usePermissions();

  const { data: fullUser } = useAdminUser(displayUser?.id ?? "");
  const { mutateAsync: updateUser } = useUpdateUser(displayUser?.id ?? "");
  const { mutateAsync: setWorkspaceMemberships } = useSetUserWorkspaceMemberships();

  const { data: workspacesData } = useAdminWorkspaces(
    { offset: 0, limit: 100 },
    { column: "name", direction: "asc" },
    {},
  );

  // The workspace → merchants role tree (UNB-2001).
  const roleTree = useWorkspaceRoleTree();
  const addWorkspaceItems = useListData<{ id: string; label: string }>({ initialItems: [] });
  const initialGrantsRef = useRef<WorkspaceRoleGrants>({});
  const grantsInitialized = useRef(false);

  // Reset when modal opens/closes
  useEffect(() => {
    if (!user) return;
    form.reset({ ...user, role: editableRole(user.role) });
    roleTree.setGrants({});
    for (const item of addWorkspaceItems.items) addWorkspaceItems.remove(item.id);
    initialGrantsRef.current = {};
    grantsInitialized.current = false;
  }, [user]);

  // Initialize grants (with roles) from full user data once it loads, once per open.
  useEffect(() => {
    if (!open || !fullUser || grantsInitialized.current) return;
    const initial = grantsFromUserWorkspaces(fullUser.workspaces ?? []);
    initialGrantsRef.current = initial;
    roleTree.setGrants(initial);
    grantsInitialized.current = true;
  }, [open, fullUser]);

  const workspacesById = useMemo(() => {
    const map = new Map<string, WorkspaceRoleTreeInfo>();
    for (const ws of workspacesData?.workspaces ?? []) {
      map.set(ws.id, { id: ws.id, name: ws.name, logoUrl: ws.logoUrl, merchants: ws.merchants });
    }
    return map;
  }, [workspacesData]);

  const availableWorkspacesToAdd = useMemo(
    () => (workspacesData?.workspaces ?? []).filter((ws) => !(ws.id in roleTree.grants)),
    [workspacesData, roleTree.grants],
  );

  const onSubmit: SubmitHandler<FormValues> = async (payload) => {
    if (!displayUser) return;
    await updateUser({ ...payload });

    const grants = roleTree.grants;
    const workspaceIds = Object.keys(grants);
    for (const wsId of workspaceIds) {
      // Workspace admins need no brands (UNB-2001); everyone else needs ≥1.
      if (!grants[wsId]?.workspaceAdmin && Object.keys(grants[wsId]?.merchants ?? {}).length === 0) {
        const name = workspacesById.get(wsId)?.name ?? wsId;
        error(`Select at least one merchant in "${name}"`);
        return;
      }
    }

    const initialGrants = initialGrantsRef.current;
    const unchanged =
      Object.keys(initialGrants).length === workspaceIds.length &&
      workspaceIds.every((wsId) => grantsEqual(initialGrants[wsId], grants[wsId]));

    if (!unchanged) {
      await setWorkspaceMemberships({
        userId: displayUser.id,
        memberships: Object.fromEntries(
          workspaceIds.map((wsId) => [
            wsId,
            {
              workspaceAdmin: grants[wsId].workspaceAdmin,
              merchants: grants[wsId].workspaceAdmin ? {} : grants[wsId].merchants,
            },
          ]),
        ),
      });
    }

    success("User updated");
    close();
  };

  return (
    // Not dismissable: an outside click would discard the whole form (UNB-2129).
    <ModalOverlay
      isOpen={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) close();
      }}
      isDismissable={false}
    >
      <Modal className="dark-mode w-xl">
        <Dialog className="w-full">
          <form onSubmit={form.handleSubmit(onSubmit)} className="w-full">
            <div className="relative w-full overflow-hidden rounded-2xl bg-primary shadow-none transition-all">
              <CloseButton onClick={close} size="lg" className="absolute top-3 right-3" />
              <div className="flex flex-col gap-4 px-4 pt-5 mr-4 sm:px-6 sm:pt-6">
                <Heading slot="title" className="text-md font-semibold text-primary">
                  Edit User
                </Heading>
              </div>
              <div className="h-5 w-full" />
              <div className="relative flex flex-col gap-4 px-4 sm:gap-5 sm:px-6">
                <FormItem className="flex flex-col">
                  <Input isRequired label="Email" placeholder="Email" value={displayUser?.email} isDisabled />
                </FormItem>
                <div className="flex flex-row justify-between gap-2">
                  <FormField
                    control={form.control}
                    name="firstName"
                    render={({ field }) => (
                      <FormItem className="flex flex-col">
                        <Input isRequired label="First Name" placeholder="First Name" {...field} />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="lastName"
                    render={({ field }) => (
                      <FormItem className="flex flex-col">
                        <Input isRequired label="Last Name" placeholder="Last Name" {...field} />
                      </FormItem>
                    )}
                  />
                </div>
                <FormField
                  control={form.control}
                  name="role"
                  render={({ field }) => (
                    <FormItem className="flex flex-col">
                      {/* Global role toggle: on → internal Unbox staff (admin), off → user. */}
                      <div className="flex items-center gap-[15px]">
                        <div className="flex items-center gap-1.5">
                          <span className="text-sm font-medium text-primary">Show Unbox console</span>
                          <Tooltip title="Grants access to the internal Unbox admin console (staff only).">
                            <TooltipTrigger>
                              <HelpCircle className="size-3.5 text-tertiary" />
                            </TooltipTrigger>
                          </Tooltip>
                        </div>
                        <Toggle
                          aria-label="Show Unbox console"
                          isSelected={field.value === "admin"}
                          isDisabled={field.value === "paused"}
                          onChange={(checked) => field.onChange(checked ? "admin" : "user")}
                        />
                      </div>
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="role"
                  render={({ field }) => (
                    <FormItem className="flex flex-col">
                      <div className="flex items-center gap-[15px]">
                        <div className="flex items-center gap-1.5">
                          <span className="text-sm font-medium text-primary">Pause access</span>
                          <Tooltip title="Paused users can log in but only see a message asking them to contact their account manager. Unpausing restores standard user access.">
                            <TooltipTrigger>
                              <HelpCircle className="size-3.5 text-tertiary" />
                            </TooltipTrigger>
                          </Tooltip>
                        </div>
                        <Toggle
                          aria-label="Pause access"
                          isSelected={field.value === "paused"}
                          onChange={(checked) => field.onChange(checked ? "paused" : "user")}
                        />
                      </div>
                    </FormItem>
                  )}
                />

                <FormItem className="flex flex-col gap-2">
                  <div className="flex items-center gap-1">
                    <span className="text-sm font-medium text-primary">Workspace access</span>
                    <Tooltip title="Assign the user to one or more workspaces, then choose their role at the workspace level or per merchant inside it.">
                      <TooltipTrigger>
                        <HelpCircle className="size-3.5 text-tertiary" />
                      </TooltipTrigger>
                    </Tooltip>
                  </div>

                  {Object.keys(roleTree.grants).length === 0 && (
                    <p className="text-sm text-tertiary">No workspace access yet — add one below.</p>
                  )}

                  <WorkspaceRoleTree
                    grants={roleTree.grants}
                    workspacesById={workspacesById}
                    invitableRoles={invitableRoles}
                    onRemoveWorkspace={roleTree.removeWorkspace}
                    onToggleBrand={roleTree.toggleBrand}
                    onSetWorkspaceRole={roleTree.setWorkspaceRole}
                    onSetMerchantRole={roleTree.setMerchantRole}
                  />

                  {availableWorkspacesToAdd.length > 0 && (
                    <MultiSelect
                      aria-label="Add workspace"
                      placeholder="Search Workspaces"
                      size="md"
                      items={availableWorkspacesToAdd.map((w) => ({ id: w.id, label: w.name }))}
                      selectedItems={addWorkspaceItems}
                      onItemInserted={(key) => {
                        const ws = workspacesById.get(String(key));
                        if (ws)
                          roleTree.addWorkspace(
                            ws.id,
                            ws.merchants.map((m) => m.id),
                          );
                        addWorkspaceItems.remove(String(key));
                      }}
                      popoverClassName="dark-mode"
                    >
                      {(item) => (
                        <MultiSelect.Item key={item.id} id={item.id} className="dark-mode">
                          {item.label}
                        </MultiSelect.Item>
                      )}
                    </MultiSelect>
                  )}
                </FormItem>
              </div>
              <div className="z-10 flex flex-1 flex-col-reverse gap-3 p-4 pt-6 *:grow sm:grid sm:grid-cols-2 sm:px-6 sm:pt-8 sm:pb-6">
                <Button color="secondary" size="lg" onClick={close} isDisabled={isSubmitting}>
                  Cancel
                </Button>
                <Button color="primary" size="lg" type="submit" isLoading={isSubmitting} isDisabled={isSubmitting}>
                  Update
                </Button>
              </div>
            </div>
          </form>
        </Dialog>
      </Modal>
    </ModalOverlay>
  );
};
